import axios from "axios";
import useAxios from "./useAxios";

const protocol = process.env.REACT_APP_API_PROTOCOL || "http://";

const apiBaseUrl = () =>
	`${protocol}${process.env.REACT_APP_API_URL}api/`;

/** Profile classification_name cached after login (empty if none). */
export const USER_CLASSIFICATION_NAME_KEY = "user_classification_name";

export const setStoredUserClassificationName = (name) => {
	localStorage.setItem(
		USER_CLASSIFICATION_NAME_KEY,
		name == null ? "" : String(name),
	);
};

export const persistAuthTokens = (access, refresh) => {
	// refresh 우선 저장 — kill 타이밍에 구 refresh만 남는 경우 완화
	if (refresh) {
		localStorage.setItem("refresh", refresh);
	}
	if (access) {
		localStorage.setItem("access", access);
	}
};

export const clearLocalAuth = () => {
	localStorage.removeItem("access");
	localStorage.removeItem("refresh");
	localStorage.removeItem("user_groups");
	localStorage.removeItem(USER_CLASSIFICATION_NAME_KEY);
};

export const hasStoredSession = () =>
	Boolean(
		localStorage.getItem("access") || localStorage.getItem("refresh"),
	);

export const isTokenValid = () => hasStoredSession();

export const getStoredToken = () => localStorage.getItem("access");

export const getStoredRefreshToken = () => localStorage.getItem("refresh");

export const decodeToken = (token) => {
	try {
		const base64Url = token.split(".")[1];
		const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split("")
				.map(function (c) {
					return (
						"%" +
						("00" + c.charCodeAt(0).toString(16)).slice(-2)
					);
				})
				.join(""),
		);
		return JSON.parse(jsonPayload);
	} catch (error) {
		console.error("Error decoding token:", error);
		return null;
	}
};

export const getCurrentUserFromToken = () => {
	const token = localStorage.getItem("access");
	if (!token) {
		return null;
	}
	return decodeToken(token);
};

export const getCurrentUser = getCurrentUserFromToken;

const isAccessTokenExpiringSoon = (token, bufferSeconds = 120) => {
	const payload = decodeToken(token);
	if (!payload?.exp) {
		return true;
	}
	return payload.exp * 1000 <= Date.now() + bufferSeconds * 1000;
};

/** Refresh endpoint 401/403 only — network errors must not wipe the session. */
export const isDefinitiveAuthFailure = (error) => {
	const status = error?.response?.status;
	return status === 401 || status === 403;
};

const REFRESH_LOCK_NAME = "smartlevite-token-refresh";
const REFRESH_LOCK_STORAGE_KEY = "auth_refresh_lock";
const REFRESH_LOCK_TTL_MS = 15000;

let refreshInFlight = null;

const requestAccessTokenRefresh = async (refresh) => {
	const response = await axios.post(
		`${apiBaseUrl()}users/token/refresh/`,
		{ refresh },
		{
			headers: {
				"Content-Type": "application/json",
			},
		},
	);

	if (!response.data.access) {
		throw new Error("Invalid refresh response");
	}

	persistAuthTokens(response.data.access, response.data.refresh);
	return response.data.access;
};

const getUsableAccessToken = (bufferSeconds = 30) => {
	const access = localStorage.getItem("access");
	if (!access || isAccessTokenExpiringSoon(access, bufferSeconds)) {
		return null;
	}
	return access;
};

const withCrossContextRefreshLock = async (fn) => {
	if (typeof navigator !== "undefined" && navigator.locks?.request) {
		return navigator.locks.request(REFRESH_LOCK_NAME, { mode: "exclusive" }, fn);
	}

	const lockId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
	const startedAt = Date.now();

	while (Date.now() - startedAt < REFRESH_LOCK_TTL_MS) {
		const existing = localStorage.getItem(REFRESH_LOCK_STORAGE_KEY);
		let holder = null;
		try {
			holder = existing ? JSON.parse(existing) : null;
		} catch {
			holder = null;
		}

		const holderExpired =
			!holder?.id ||
			!holder?.at ||
			Date.now() - holder.at > REFRESH_LOCK_TTL_MS;

		if (holderExpired) {
			localStorage.setItem(
				REFRESH_LOCK_STORAGE_KEY,
				JSON.stringify({ id: lockId, at: Date.now() }),
			);
			await new Promise((resolve) => setTimeout(resolve, 20));
			const confirmed = localStorage.getItem(REFRESH_LOCK_STORAGE_KEY);
			try {
				if (confirmed && JSON.parse(confirmed).id === lockId) {
					try {
						return await fn();
					} finally {
						const current = localStorage.getItem(
							REFRESH_LOCK_STORAGE_KEY,
						);
						try {
							if (
								current &&
								JSON.parse(current).id === lockId
							) {
								localStorage.removeItem(
									REFRESH_LOCK_STORAGE_KEY,
								);
							}
						} catch {
							localStorage.removeItem(
								REFRESH_LOCK_STORAGE_KEY,
							);
						}
					}
				}
			} catch {
				// contending writers — retry
			}
		}

		await new Promise((resolve) => setTimeout(resolve, 50));
	}

	// 락 획득 실패 시에도 진행 (다른 컨텍스트가 이미 갱신했을 수 있음)
	return fn();
};

export const refreshToken = async () => {
	if (refreshInFlight) {
		return refreshInFlight;
	}

	refreshInFlight = (async () => {
		try {
			return await withCrossContextRefreshLock(async () => {
				// 락 획득 후 다른 탭/PWA가 이미 갱신했는지 확인
				const alreadyFresh = getUsableAccessToken(60);
				if (alreadyFresh) {
					return alreadyFresh;
				}

				const refresh = localStorage.getItem("refresh");
				if (!refresh) {
					throw new Error("No refresh token available");
				}

				try {
					return await requestAccessTokenRefresh(refresh);
				} catch (error) {
					if (!isDefinitiveAuthFailure(error)) {
						throw error;
					}

					// 다른 컨텍스트가 회전했을 수 있음 → 최신 토큰으로 1회 재시도
					const latestAccess = getUsableAccessToken(60);
					if (latestAccess) {
						return latestAccess;
					}

					const latestRefresh = localStorage.getItem("refresh");
					if (latestRefresh && latestRefresh !== refresh) {
						return await requestAccessTokenRefresh(
							latestRefresh,
						);
					}

					throw error;
				}
			});
		} finally {
			refreshInFlight = null;
		}
	})();

	return refreshInFlight;
};

export const ensureValidSession = async () => {
	const refresh = localStorage.getItem("refresh");
	if (!refresh) {
		return false;
	}

	const access = localStorage.getItem("access");
	if (!access || isAccessTokenExpiringSoon(access)) {
		try {
			await refreshToken();
		} catch (error) {
			console.error("Session refresh failed:", error);
			if (isDefinitiveAuthFailure(error)) {
				clearLocalAuth();
				return false;
			}
			return true;
		}
	}

	return true;
};

export const login = async (username, password) => {
	try {
		const response = await useAxios.post("users/token/", {
			username,
			password,
		});

		if (response.data.access && response.data.refresh) {
			persistAuthTokens(
				response.data.access,
				response.data.refresh,
			);
			return {
				...response.data,
				must_change_password: Boolean(
					response.data.must_change_password,
				),
			};
		}

		throw new Error("Invalid response from server");
	} catch (error) {
		console.error("Login error:", error);
		throw error;
	}
};

export const logoutFromServer = async () => {
	const refresh = localStorage.getItem("refresh");

	try {
		if (refresh) {
			await axios.post(
				`${apiBaseUrl()}users/logout/`,
				{ refresh },
				{
					headers: {
						"Content-Type": "application/json",
					},
				},
			);
		}
	} catch (error) {
		console.error("Server logout failed:", error);
	} finally {
		clearLocalAuth();
	}
};

/** @deprecated Prefer logoutFromServer for explicit sign-out. */
export const logout = () => {
	clearLocalAuth();
};

export const getCurrentUserProfile = async () => {
	try {
		const response = await useAxios.get("users/profile/");
		return response.data;
	} catch (error) {
		console.error("Error fetching user profile:", error);
		throw error;
	}
};

export const getUserGroups = async () => {
	try {
		const response = await useAxios.get("users/groups/me/");
		return response.data.groups || [];
	} catch (error) {
		console.error("Error fetching user groups:", error);
		return [];
	}
};
